import { Box, Typography } from "@mui/material";
import { FAULT_COLORS } from "../../utils/faults";

const SEVERITY_ORDER = ["critical", "high", "moderate", "healthy"];

/**
 * FaultDashboard
 *
 * Displays a segmented severity bar, severity pill counts, and a health
 * percentage for any array of items that carry a `.fault` property.
 *
 * Works for both tiers (Equipment.jsx) and components (PanelComponents.jsx).
 *
 * Props:
 *   items      {Array}  — array of tier or component objects with optional `.fault`
 *   itemLabel  {string} — singular noun used in the count label (default: "item")
 */
const FaultDashboard = ({ items = [], itemLabel = "item" }) => {
  const total = items.length;
  if (total === 0) return null;

  const counts = SEVERITY_ORDER.reduce((acc, sev) => {
    acc[sev] = items.filter((t) =>
      sev === "healthy" ? !t.fault : t.fault?.severity === sev,
    ).length;
    return acc;
  }, {});

  const healthyPct = Math.round((counts.healthy / total) * 100);

  const healthColor =
    healthyPct >= 80
      ? FAULT_COLORS.healthy
      : healthyPct >= 50
        ? FAULT_COLORS.moderate
        : FAULT_COLORS.critical;

  return (
    <Box
      sx={{
        mx: 2,
        mb: 2,
        mt: 1,
        borderRadius: "12px",
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "#fafafa",
        px: 2.5,
        py: 1.5,
      }}
    >
      {/* Header row */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        <Typography
          sx={{
            fontSize: 15,
            fontWeight: 700,
            letterSpacing: 1,
            textTransform: "uppercase",
            color: "text.disabled",
          }}
        >
          Fault Summary
        </Typography>
        <Box sx={{ flex: 1, height: "1px", bgcolor: "divider" }} />
        <Typography sx={{ fontSize: 15, color: "text.disabled" }}>
          {total} {itemLabel}{total !== 1 ? "s" : ""}
        </Typography>
      </Box>

      {/* Segmented severity bar */}
      <Box
        sx={{
          display: "flex",
          height: 6,
          borderRadius: 99,
          overflow: "hidden",
          mb: 1.5,
          gap: "2px",
          bgcolor: "divider",
        }}
      >
        {SEVERITY_ORDER.map((sev) => {
          const pct = (counts[sev] / total) * 100;
          if (pct === 0) return null;
          return (
            <Box
              key={sev}
              sx={{
                height: "100%",
                width: `${pct}%`,
                bgcolor: FAULT_COLORS[sev],
                transition: "width 0.5s ease",
              }}
            />
          );
        })}
      </Box>

      {/* Severity pills + health score */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1,
        }}
      >
        {SEVERITY_ORDER.map((sev) => (
          <Box
            key={sev}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.6,
              px: 1.2,
              py: 0.35,
              borderRadius: 99,
              bgcolor: `${FAULT_COLORS[sev]}15`,
              border: "1px solid",
              borderColor: `${FAULT_COLORS[sev]}35`,
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                bgcolor: FAULT_COLORS[sev],
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 700,
                color: FAULT_COLORS[sev],
                lineHeight: 1,
              }}
            >
              {counts[sev]}
            </Typography>
            <Typography
              sx={{
                fontSize: 15,
                color: "text.secondary",
                textTransform: "capitalize",
                lineHeight: 1,
              }}
            >
              {sev}
            </Typography>
          </Box>
        ))}

        <Box sx={{ flex: 1 }} />

        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
          <Typography
            sx={{
              fontSize: 15,
              color: "text.disabled",
              letterSpacing: 0.5,
              textTransform: "uppercase",
            }}
          >
            Health
          </Typography>
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 800,
              color: healthColor,
              lineHeight: 1,
              transition: "color 0.3s ease",
            }}
          >
            {healthyPct}%
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default FaultDashboard;